import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  forwardRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { TranslatePipe } from '@shared/pipes/translate.pipe';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { MenuItem, MenuLeafActivation, findOpenableChild, isEmptiedFolder, isModuleShortcutLabel, menuItemKey } from '@core/models/menu.model';
import { menuItemHasExternalLaunchVisual } from '@core/utilities/external-launch.util';
import { MenuFocusService } from '@core/services/menu-focus.service';

const FLYOUT_GAP = 4; // horizontal gap between cascaded flyouts
const VIEWPORT_PADDING = 8; // minimum distance from the viewport edge

/**
 * Lateral submenu panel rendered as a cascade.
 *
 * Recursive: when the user clicks an item with children, this component
 * renders another instance of itself positioned to the right of the item.
 * If the next flyout does not fit on the right, it flips to the left.
 *
 * Positioning uses `position: fixed` computed in JavaScript from the
 * `getBoundingClientRect()` of the anchor — no CDK Overlay, to keep the
 * dependency tree slim.
 *
 * Basic accessibility:
 * - Each panel is `role="menu"`; items are `role="menuitem"`.
 * - Parent items expose `aria-haspopup="menu"` and `aria-expanded`.
 *
 * Keyboard navigation (arrows, Esc, Home/End) is implemented in `onItemKeydown`.
 */
@Component({
  selector: 'app-nav-flyout',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    TranslatePipe,
    TooltipDirective,
    forwardRef(() => NavFlyoutComponent)
  ],
  templateUrl: './nav-flyout.component.html',
  styleUrl: './nav-flyout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavFlyoutComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) items: MenuItem[] = [];
  /** Anchor element (parent button) used to compute the initial position. */
  @Input() anchor: HTMLElement | null = null;
  /**
   * Flyout depth (1 = first level), handed down incremented to the recursive
   * child and published on the panel as `data-level` — see the note beside that
   * binding in the template before touching either.
   */
  @Input() level: number = 1;
  /** IDs of items in the active path, for recursive visual highlight. */
  @Input() activePath: ReadonlySet<string | number> = new Set();
  /** Current URL (without query string), used to highlight the active leaf with the brand gradient. */
  @Input() currentUrl: string = '';
  /**
   * When `true`, the panel moves focus to the first item right after opening.
   * Used when the flyout is opened via keyboard (ArrowRight or Enter).
   */
  @Input() autofocus: boolean = false;

  /**
   * Fired when the user picks a leaf. Emits the chosen item together with the
   * gesture that picked it, because where the focus ends up depends on which
   * one it was: a keypress is inside the focus contract (AC19) and a click is
   * not (D7), and the consumer cannot tell them apart on its own — a click
   * focuses the button it lands on, so the DOM reads the same either way.
   * Levels forward the gesture untouched, so a leaf three levels down reaches
   * the sidebar describing the gesture that actually picked it.
   */
  @Output() itemActivated = new EventEmitter<MenuLeafActivation>();
  /**
   * Request to close THIS level alone, handing the keyboard back to the item
   * that opened it — the parent item one level up, or the root item when this
   * is the level-1 panel (ArrowLeft, RN-05).
   */
  @Output() closeRequested = new EventEmitter<void>();
  /**
   * Request to close the WHOLE cascade, handing the keyboard back to the root
   * item that anchors it (Esc, RN-06). Passed up level by level, because only
   * the sidebar knows that root item. Keeping it apart from `closeRequested`
   * is what makes the two keys distinguishable from inside the panel (C-06).
   */
  @Output() closeAllRequested = new EventEmitter<void>();

  @ViewChild('panel', { static: true }) panelRef!: ElementRef<HTMLDivElement>;
  @ViewChildren('itemBtn') itemButtons!: QueryList<ElementRef<HTMLButtonElement>>;

  /** Parent at the current level whose children are open. */
  activeChild: MenuItem | null = null;

  /**
   * Item of THIS level whose child panel the KEYBOARD opened — `null` when the
   * cascade below is the mouse's, or when nothing is open below.
   *
   * Held as the item rather than as a boolean, for the reason
   * `SidebarComponent.keyboardOpenedItem` documents one level up: "the keyboard
   * opened what is on screen" is a fact about the CURRENT child, and a flag
   * would need a reset point at every place `activeChild` changes.
   */
  private keyboardOpenedChild: MenuItem | null = null;

  /**
   * Whether the child level this panel mounts owes the keyboard its first item.
   *
   * The child used to be handed THIS panel's own `autofocus` straight down, so
   * after a single keyboard opening every level below it — whatever gesture put
   * it there — declared an opening the keyboard had asked for and pulled the
   * focus into itself, off wherever the user had left it (cause C-05, AC8).
   * The intent belongs to ONE opening and to the gesture that made it: the
   * keyboard records itself here, the mouse never does.
   *
   * Compared by stable key rather than by reference, because `rebindOpenChild`
   * re-points `activeChild` at every re-render while `keyboardOpenedChild` goes
   * on naming the object the key was pressed on — the C-04 shape.
   *
   * **That comparison is defence in depth here, and it is NOT observable: a
   * mutation to `===` leaves the suite green, and no test can be written that
   * turns it red.** Said plainly because the sidebar's twin, `flyoutAutofocus`,
   * IS discriminated by a test, and the two must not be read as equally proved.
   * The reason is the consumer: this getter feeds exactly one binding, the
   * `[autofocus]` of the child panel, and neither transition the mutation
   * produces changes what the reader sees. Going `true -> false` on a live
   * child is ignored — the child arms its latch on `false -> true` only. Going
   * `false -> true` on a later key re-arms a latch that lands the keyboard on
   * the same first item the parent's own `focusFirstChildItem` already lands it
   * on, and that call follows `openChildFromKeyboard` at BOTH of its call
   * sites, so the child's own autofocus is redundant with it on every keyboard
   * opening. It stays because the rule for "the same item across a re-render"
   * has one spelling in this codebase, and a second one written by hand here
   * would be the drift `menuItemKey` exists to prevent.
   */
  get childAutofocus(): boolean {
    return this.keyboardOpenedChild !== null
      && this.activeChild !== null
      && menuItemKey(this.keyboardOpenedChild) === menuItemKey(this.activeChild);
  }

  /** `top`/`left` style applied to the panel. Computed in `calculatePosition()`. */
  panelStyle: Record<string, string> = { top: '0px', left: '0px', visibility: 'hidden' };

  /** Delay before opening a nested submenu on hover, mirroring the sidebar root. */
  private static readonly HOVER_DELAY_MS = 100;
  /** Pending hover timer (cancelled on mouseleave or component destroy). */
  private hoverTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Handle of the pending re-measure. Tracked so a teardown can drop it: an
   * untracked one fires against a destroyed view, and `calculatePosition`
   * flushes change detection and schedules a focus handoff — both AFTER
   * `ngOnDestroy` released the one this panel owned, which is cause C-01.
   */
  private repositionHandle: ReturnType<typeof setTimeout> | null = null;

  private resizeListener: (() => void) | null = null;
  private scrollListener: (() => void) | null = null;

  /**
   * Whether the opening autofocus is still owed. Consumed the first time the
   * panel becomes visible, because `calculatePosition` also runs on every
   * re-position — `resize`, `scroll` and `ngOnChanges` — and only the opening
   * is allowed to move the keyboard.
   */
  private openingFocusPending = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private menuFocus: MenuFocusService
  ) {}

  ngAfterViewInit(): void {
    this.openingFocusPending = this.autofocus;
    this.calculatePosition();
    // Re-position on scroll/resize to follow anchors inside scrollable containers.
    this.zone.runOutsideAngular(() => {
      this.resizeListener = () => this.calculatePosition();
      this.scrollListener = () => this.calculatePosition();
      window.addEventListener('resize', this.resizeListener, { passive: true });
      window.addEventListener('scroll', this.scrollListener, { passive: true, capture: true });
    });
  }

  /**
   * When `anchor` or `items` change (e.g. the user moved to another root
   * item without closing the flyout), re-position the panel to align with
   * the new anchor AND collapse any open child cascade. Without resetting
   * `activeChild`, the previous deepest level (e.g. Autolac > Dashboards >
   * Indicadores > Monitoramento) stayed mounted next to the new flyout.
   */
  ngOnChanges(changes: SimpleChanges): void {
    // An `autofocus` that goes false -> true on a live instance IS the keyboard
    // asking for this panel: the sidebar flips it without touching `items` when
    // the panel is already open on that root item — `SidebarComponent.onRootKeydown`
    // guards the `openItem`/`openAnchor` assignment and records the keyboard's
    // intent outside that guard. Watching `items`/`anchor` alone left that
    // ArrowRight — and every one after it — a silent no-op.
    const autofocusRequested =
      !!changes['autofocus'] && !changes['autofocus'].firstChange && this.autofocus;
    if (autofocusRequested && this.panelRef) {
      this.openingFocusPending = true;
      this.scheduleReposition();
    }
    const anchorChanged = !!changes['anchor'] && !changes['anchor'].firstChange;
    const itemsChanged = !!changes['items'] && !changes['items'].firstChange;
    if (!this.panelRef || (!anchorChanged && !itemsChanged)) return;
    this.cancelHoverTimer();
    // The anchor IS the parent item this panel belongs to, so a change of
    // anchor is the panel being taken over by another branch. A new `items`
    // ARRAY on the same anchor is not: it is this very branch re-rendered — a
    // permission check resolving, a language switch — where every object in the
    // tree is rebuilt while the branch on screen stays the same one (cause
    // C-04). Reading the array reference as an opening blanked the panel out
    // from under the keyboard and pulled it back to the first item, on a menu
    // the user had not touched (AC9).
    if (anchorChanged) {
      // The `*ngIf` that mounts this panel from `SidebarComponent.openItem`
      // keeps the instance alive when the user moves to another root item and
      // only swaps the inputs, so an opening never comes back through
      // `ngAfterViewInit`. Arming the latch only there left every reopening
      // with the keyboard on the previous root item (AC5, "on every opening").
      this.activeChild = null;
      if (itemsChanged) this.openingFocusPending = this.autofocus;
      // Mark hidden until the next measurement to avoid flicker on the new spot.
      this.panelStyle = { ...this.panelStyle, visibility: 'hidden' };
    } else {
      // Only `items` changed: this very branch was rebuilt under the panel.
      // The cascade below it stays, and has to be re-pointed at the tree that
      // was just handed in — see `rebindOpenChild`.
      this.rebindOpenChild();
    }
    this.cdr.markForCheck();
    this.scheduleReposition();
  }

  /**
   * Re-points the open child at the item of the tree just handed in, matched by
   * stable key.
   *
   * `activeChild` is the object the user opened, and every emission of the menu
   * rebuilds the tree from the DTOs — so the recursive `<app-nav-flyout>` went
   * on being fed `activeChild.children` of the PREVIOUS emission: the level
   * below kept the labels of the old language and kept rendering, still
   * clickable, items the permission check had just denied (cause C-04). A
   * branch that did not survive the re-render does not get to keep a panel on
   * screen below it either — the mirror of
   * `SidebarComponent.rebindOpenCascade`, one level down.
   */
  private rebindOpenChild(): void {
    if (this.activeChild === null) return;
    this.activeChild = findOpenableChild(this.items, menuItemKey(this.activeChild));
  }

  /**
   * Defers the measurement one frame, so the DOM reflects the new inputs before
   * the panel reads its own size. Superseding, not stacking: two input changes
   * in the same pass used to leave two timers racing each other.
   */
  private scheduleReposition(): void {
    this.cancelReposition();
    this.repositionHandle = setTimeout(() => {
      this.repositionHandle = null;
      this.calculatePosition();
    });
  }

  private cancelReposition(): void {
    if (this.repositionHandle === null) return;
    clearTimeout(this.repositionHandle);
    this.repositionHandle = null;
  }

  ngOnDestroy(): void {
    if (this.resizeListener) window.removeEventListener('resize', this.resizeListener);
    if (this.scrollListener) window.removeEventListener('scroll', this.scrollListener, { capture: true });
    this.cancelHoverTimer();
    this.cancelReposition();
    // Release only the focus handoff this panel owns: the change detection that
    // destroys it is the same one that runs after whoever closed it already
    // scheduled taking the focus back (cause C-01).
    this.menuFocus.cancel(this);
  }

  trackByItem = (_index: number, item: MenuItem): string => menuItemKey(item);

  /**
   * `menuItemKey` as something the template can call, so `data-item-id` is
   * WRITTEN by the same rule `MenuFocusService` READS it back with. See the
   * function's own doc: an inline `item.id ?? item.label` in the binding is one
   * edit away from disagreeing with it.
   */
  itemKey = menuItemKey;

  hasChildren(item: MenuItem): boolean {
    return !!item.children && item.children.length > 0;
  }

  isOnActivePath(item: MenuItem): boolean {
    const key = item.id ?? item.label;
    return this.activePath.has(key);
  }

  /**
   * Active leaf: this item's route matches the current URL. Applies the brand
   * gradient. "Módulo X" shortcuts are explicitly excluded — they open the
   * underlying module in a new tab and never represent the current page, even
   * when their route is a prefix of the current URL by coincidence.
   */
  isActiveRoute(item: MenuItem): boolean {
    if (this.isModuleShortcut(item)) return false;
    if (!item.route || !this.currentUrl) return false;
    return this.currentUrl === item.route || this.currentUrl.startsWith(item.route + '/');
  }

  /**
   * "Module shortcut" item — labels starting with "Módulo " (e.g. "Módulo
   * Atendimento", "Módulo Autolac", "Módulo Financeiro"). Receives a bottom
   * divider to separate them visually from sibling pages/folders.
   */
  isModuleShortcut(item: MenuItem): boolean {
    return isModuleShortcutLabel(item.labelKey ?? item.label);
  }

  showsExternalLaunchIcon(item: MenuItem): boolean {
    return menuItemHasExternalLaunchVisual(item);
  }

  /**
   * Compared by stable key, never by reference: a re-render of this branch
   * hands the panel an `items` array of brand-new objects while the cascade
   * below stays on screen, and an identity check then reported every item as
   * closed — taking the open chain that `aria-expanded` publishes down with it
   * (cause C-04).
   */
  isOpenParent(item: MenuItem): boolean {
    return this.activeChild !== null && menuItemKey(this.activeChild) === menuItemKey(item);
  }

  onItemClick(item: MenuItem, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.cancelHoverTimer();

    if (this.hasChildren(item)) {
      // Toggle: clicking the same parent again collapses its chain. Recognised
      // by stable key through `isOpenParent`, never by reference: `activeChild`
      // holds the object that opened the cascade and `item` comes from the
      // array being rendered, and a re-render makes those two different objects
      // with the same key — the click that meant "close" then re-opened the
      // level (cause C-04).
      const opening = !this.isOpenParent(item);
      this.activeChild = opening ? item : null;
      this.declareTheChildLevelTheMouses();
      this.cdr.markForCheck();
      // The click path used to stop at `markForCheck`, from inside the very
      // change-detection pass that then cleared the flag — so the child level
      // was mounted and left `visibility: hidden`, on screen for nobody until
      // some unrelated event flushed it. Hover published its own child all
      // along; the two mouse paths into a level have to be one.
      if (opening) this.flushChildVisibility();
      return;
    }

    this.itemActivated.emit({ item, fromKeyboard: false });
  }

  /**
   * Hover on a folder item inside the flyout: opens its submenu after
   * `HOVER_DELAY_MS`, mirroring the sidebar root behaviour. Because the
   * component is recursive, this hook covers every nesting level
   * (Faturamento, Configurações, Dashboards, Indicadores, …) automatically.
   *
   * Uses `cdr.detectChanges()` for the same reason as the sidebar handler:
   * timer callbacks may fall outside the natural zone tick and `markForCheck`
   * alone wasn't reliably triggering the recursive `<app-nav-flyout>` ngIf.
   */
  onItemMouseEnter(item: MenuItem, _event: MouseEvent): void {
    if (!this.hasChildren(item)) return; // leaves do not open a submenu
    if (this.isOpenParent(item)) return; // already open — nothing to do (by key, C-04)
    this.cancelHoverTimer();
    this.hoverTimer = setTimeout(() => {
      this.zone.run(() => {
        this.hoverTimer = null;
        this.activeChild = item;
        this.declareTheChildLevelTheMouses();
        this.cdr.detectChanges(); // mounts the recursive <app-nav-flyout> child
        // The mounted child's ngAfterViewInit fires synchronously inside the
        // CD above, sets its panelStyle and calls markForCheck. We need a
        // follow-up CD pass to flush that to the DOM — without it the panel
        // only appears on the next bound event (mouseleave, hover sibling).
        Promise.resolve().then(() => this.cdr.detectChanges());
      });
    }, NavFlyoutComponent.HOVER_DELAY_MS);
  }

  /** Mouse leaves the item: cancel any pending open. Already-open submenus stay. */
  onItemMouseLeave(): void {
    this.cancelHoverTimer();
  }

  private cancelHoverTimer(): void {
    if (this.hoverTimer === null) return;
    clearTimeout(this.hoverTimer);
    this.hoverTimer = null;
  }

  /** When a leaf is activated in a descendant, bubble it up to the sidebar. */
  onChildItemActivated(activation: MenuLeafActivation): void {
    this.itemActivated.emit(activation);
  }

  /**
   * WAI-ARIA Menu pattern (vertical):
   * - ArrowDown/Up: focus next/previous (with wrap-around).
   * - Home/End: focus first/last.
   * - Enter/Space: activate (open submenu or emit leaf item).
   * - ArrowRight: if the item has children, open the submenu and focus its first item.
   * - ArrowLeft: emits `closeRequested` — ONE level, whatever the depth. The
   *   parent level closes this panel and takes the keyboard back; on the
   *   level-1 panel the parent IS the sidebar (RN-05).
   * - Escape: emits `closeAllRequested` — the WHOLE cascade, never one level.
   *   Passed up from level to level until it reaches the sidebar, the only
   *   holder of the root item the keyboard has to land on (RN-06). The two
   *   keys are NOT the same request: collapsing them into one was C-06.
   */
  onItemKeydown(item: MenuItem, event: KeyboardEvent, index: number): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusItemByIndex((index + 1) % this.items.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusItemByIndex((index - 1 + this.items.length) % this.items.length);
        break;
      case 'Home':
        event.preventDefault();
        this.focusItemByIndex(0);
        break;
      case 'End':
        event.preventDefault();
        this.focusItemByIndex(this.items.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.hasChildren(item)) {
          this.openChildFromKeyboard(item);
          // The child renders on the change-detection pass this keystroke
          // triggers; the handoff resolves it only when it runs.
          this.focusFirstChildItem();
        } else if (!isEmptiedFolder(item)) {
          // A folder the permission check emptied is neither a parent to open
          // nor a leaf to activate: it has no panel to mount and no route to
          // follow, and emitting for it made the sidebar close the whole
          // cascade around the keyboard (AC11, AC4). The key is answered and
          // nothing else happens.
          this.itemActivated.emit({ item, fromKeyboard: true });
        }
        break;
      case 'ArrowRight':
        if (!this.hasChildren(item)) return;
        event.preventDefault();
        this.openChildFromKeyboard(item);
        this.focusFirstChildItem();
        break;
      case 'ArrowLeft':
        // One level only, whatever the depth: the parent level closes this
        // panel and takes the keyboard back, and on the level-1 panel the
        // parent IS the sidebar — same request, different consumer.
        event.preventDefault();
        this.closeRequested.emit();
        break;
      case 'Escape':
        // The whole cascade, never one level (RN-06 vs RN-05). Consuming the
        // key here is unconditional only in appearance: the item that received
        // the keydown is a menu item, which is precisely the condition the
        // sidebar's document-level Escape has to test for itself (AC12).
        event.preventDefault();
        this.closeAllRequested.emit();
        break;
    }
  }

  /**
   * Opens `item`'s child level for a key press, recording that the KEYBOARD is
   * the one asking — the record `childAutofocus` reads, and the only way a
   * child panel is ever owed the focus.
   */
  private openChildFromKeyboard(item: MenuItem): void {
    this.activeChild = item;
    this.keyboardOpenedChild = item;
    this.cdr.markForCheck();
  }

  /**
   * States that the child level on screen owes the keyboard nothing — what
   * every MOUSE opening of this level has to say.
   *
   * The record of a keyboard opening survives the cascade it describes:
   * `ArrowLeft` takes the level below down without touching it, and
   * `childAutofocus` would then read true again the moment the cursor re-opened
   * that same folder, dragging the focus off the item the user is standing on
   * (AC8). Stale is harmless as long as every gesture that opens a level says
   * whose it is.
   */
  private declareTheChildLevelTheMouses(): void {
    this.keyboardOpenedChild = null;
  }

  /**
   * Request from a deeper level to close the whole cascade: passed straight up,
   * because the item the keyboard has to land on is the root one and only the
   * sidebar has it. Each level closes as it is torn down, so nothing else is
   * owed here.
   */
  onChildCloseAllRequested(): void {
    this.closeAllRequested.emit();
  }

  /** Request from the open child: close that level and re-focus the parent item. */
  onChildCloseRequested(): void {
    if (!this.activeChild) return;
    const closed = this.activeChild;
    this.activeChild = null;
    this.cdr.markForCheck();
    // Re-focus the parent item whose submenu just closed, INSIDE this keystroke:
    // the item is already mounted in this panel and is not part of what the
    // closing level takes down. Deferring a return whose target already exists
    // makes the focus transit through `<body>` — the change detection that
    // unmounts the child runs before the timer — which is the very symptom this
    // WI closes, and it is why the same rule governs `onFlyoutClose()`.
    //
    // Located by stable key, never by `indexOf`: a re-render of this branch
    // hands the panel new objects while `activeChild` still points at the old
    // one, and the reference lookup then returned -1 — silently, so the return
    // was skipped and the keyboard fell to `<body>` all the same (C-04).
    const key = menuItemKey(closed);
    const idx = this.items.findIndex(item => menuItemKey(item) === key);
    if (idx >= 0) this.focusItemByIndex(idx);
  }

  /**
   * Moves the keyboard focus to the item at `index` **now**, dropping any
   * pending handoff. Sibling navigation stays inside the keystroke that asked
   * for it: the item already exists, and deferring it loses keys to auto-repeat
   * (TASK-61043, AC14).
   */
  private focusItemByIndex(index: number): void {
    this.menuFocus.focusNow(this.itemButtonAt(index));
  }

  /**
   * Hands the keyboard focus to the item at `index` through the menu's single
   * focus owner, resolved when the handoff runs. For the moves that cross a
   * render — panel opening, child level closing — so a newer keystroke cancels
   * this one instead of the two racing (TASK-61043, AC14).
   */
  private scheduleItemFocus(index: number): void {
    this.menuFocus.scheduleFocus({ resolve: () => this.itemButtonAt(index) }, this);
  }

  /** Item button at `index` as this panel is rendered at this instant. */
  private itemButtonAt(index: number): HTMLElement | null {
    const buttons = this.itemButtons?.toArray() ?? [];
    if (index < 0 || index >= buttons.length) return null;
    return buttons[index].nativeElement;
  }

  /**
   * Hands the focus to the first item of the open child level — chained on the
   * child's visibility, exactly like the opening of this panel is.
   *
   * The child level decides `visibility: visible` inside its own
   * `calculatePosition`, and that style reaches the DOM only on the
   * change-detection pass AFTER the one that mounted it. Without the passes
   * below, this panel's handoff — a macrotask — ran while the child was still
   * `visibility: hidden` and the browser silently dropped the `focus()` (cause
   * C-02). It only ever looked fine because a child opened from a
   * keyboard-opened panel inherits `autofocus` and flushes itself; a cascade
   * opened by hover or click and then driven with ArrowRight had no one to do
   * it, and the keyboard fell to `<body>`. The opening focus path is one,
   * whatever opened the parent.
   */
  private focusFirstChildItem(): void {
    this.flushChildVisibility();
    this.menuFocus.scheduleFocus({ resolve: () => this.firstChildItemElement() }, this);
  }

  /**
   * Publishes the child level this panel has just opened into the DOM.
   *
   * Two passes, because they publish two different views: the first
   * materialises the child (the `*ngIf` on `activeChild`), the second publishes
   * the `visibility: visible` the child decided in its own `ngAfterViewInit` —
   * a hook that runs after the child's view was already checked, so a single
   * pass can never carry it. One pass only ever looked enough because
   * `onMicrotaskEmpty` -> `ApplicationRef.tick()` supplied the second, a zone.js
   * dependency this component does not declare.
   *
   * Shared by every gesture that opens a level, so none of them can be the one
   * that leaves it hidden.
   */
  private flushChildVisibility(): void {
    Promise.resolve()
      .then(() => this.cdr.detectChanges())
      .then(() => this.cdr.detectChanges());
  }

  /**
   * First item of the open child (querySelector — the child is OnPush and may
   * still be mounting).
   *
   * Anchored on the child host with `:scope >`, never on a descendant
   * selector. `app-nav-flyout app-nav-flyout button` is matched in DOCUMENT
   * context, so from level 2 down THIS panel's own buttons satisfy it too —
   * they also carry two flyout ancestors — and they come first in document
   * order. The handoff then landed back on the item the user pressed the key
   * on while the deeper level opened unfocused. Level 1 never showed it: a root
   * panel's buttons have a single flyout ancestor.
   */
  private firstChildItemElement(): HTMLElement | null {
    return this.panelRef.nativeElement.parentElement?.querySelector<HTMLElement>(
      ':scope > app-nav-flyout button.menu-flyout__item'
    ) ?? null;
  }

  /** Anchor element of the open child, so the next level can position itself against it. */
  getActiveChildAnchor(): HTMLElement | null {
    if (!this.activeChild) return null;
    // Same rule as the binding that wrote the attribute — never a second
    // spelling of it.
    const key = menuItemKey(this.activeChild);
    return this.panelRef.nativeElement.querySelector<HTMLElement>(`[data-item-id="${CSS.escape(key)}"]`);
  }

  private calculatePosition(): void {
    if (!this.anchor) return;
    const anchorRect = this.anchor.getBoundingClientRect();
    const panel = this.panelRef.nativeElement;

    // Make sure it is hidden before measuring (first pass starts hidden to avoid flicker).
    if (this.panelStyle['visibility'] === 'hidden') {
      panel.style.visibility = 'hidden';
    }
    const panelWidth = panel.offsetWidth;
    const panelHeight = panel.offsetHeight;

    // Centre the panel vertically with the anchor (clamps below keep it inside the viewport).
    let top = anchorRect.top + anchorRect.height / 2 - panelHeight / 2;
    let left = anchorRect.right + FLYOUT_GAP;

    // Horizontal flip when overflowing the right edge.
    if (left + panelWidth > window.innerWidth - VIEWPORT_PADDING) {
      left = anchorRect.left - panelWidth - FLYOUT_GAP;
    }
    // Make sure it does not leave the left edge.
    if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;

    // Vertical adjustment: stick to the bottom edge if it overflows.
    if (top + panelHeight > window.innerHeight - VIEWPORT_PADDING) {
      top = Math.max(VIEWPORT_PADDING, window.innerHeight - panelHeight - VIEWPORT_PADDING);
    }
    if (top < VIEWPORT_PADDING) top = VIEWPORT_PADDING;

    this.panelStyle = {
      top: `${Math.round(top)}px`,
      left: `${Math.round(left)}px`,
      visibility: 'visible'
    };
    this.cdr.markForCheck();
    this.applyOpeningAutofocus();
  }

  /**
   * Keyboard flow: hands the focus to the first item when the panel opens via
   * key press — chained on the visibility, never in parallel with it.
   *
   * The `visibility: visible` decided just above only reaches the DOM on the
   * next change-detection pass, and the browser silently ignores `focus()` on a
   * subtree that is still hidden (cause C-02). So the flush comes first, in the
   * microtask the component already uses for exactly this
   * (`onItemMouseEnter`), and the handoff — a macrotask the event loop orders
   * after every microtask — finds a panel that is on screen.
   *
   * Deferred through the menu's focus owner rather than applied here, so a
   * keystroke arriving while the panel opens cancels this handoff instead of
   * racing it (AC14).
   */
  private applyOpeningAutofocus(): void {
    if (!this.openingFocusPending) return;
    this.openingFocusPending = false;
    this.flushOwnVisibility();
    this.scheduleItemFocus(0);
  }

  /**
   * Publishes THIS panel's own `visibility: visible` into the DOM — its own
   * view, never a child's — in the microtask the component already uses for
   * exactly this (`onItemMouseEnter`). One pass is enough here because the
   * style was decided in `calculatePosition`, before the pass runs; a child's
   * visibility is decided inside the pass that mounts it and needs the second
   * one `focusFirstChildItem` performs.
   */
  private flushOwnVisibility(): void {
    Promise.resolve().then(() => this.cdr.detectChanges());
  }
}
